import {
  DropdownItem,
  FormField,
  HighlightIcon,
  type HighlightIconVariant,
  type IconName,
  Select,
} from '@dev-spendesk/grapes';
import { isNotNullable } from '@spendesk/general-type-helpers/misc';
import { useEffect, useState } from 'react';

import { useTranslation } from 'src/core/common/hooks/useTranslation';

import { useAccountingSoftwareName } from '../../../apis';
import { type SyncMode } from '../../../apis/useSetSyncSettings';

type Props = {
  value: SyncMode;
  fieldName: string | undefined;
  onChange: (value: SyncMode) => void;
  allowedSyncSettings?: SyncMode[];
};

type SyncOption = {
  key: SyncMode;
  label: string;
  notes: string[];
  iconName: IconName;
  iconVariant: HighlightIconVariant;
};

const SyncOptionHelpText = ({ notes }: { notes: string[] }) => {
  return (
    <ul className="body-s text-content-secondary-bg-primary ml-16 list-disc">
      {notes.map((note, key) => (
        <li key={`SyncOptionDdl_HelpTextOption_${key + 1}`}>{note}</li>
      ))}
    </ul>
  );
};

export const SyncSettingsDropdown = ({
  value,
  fieldName,
  onChange,
  allowedSyncSettings,
}: Props) => {
  const { t } = useTranslation('global');
  const { accountingSoftwareName } = useAccountingSoftwareName();
  const pullLabel = t(
    'bookkeep.integrations.settings.analyticalFieldMapping.syncSettings.pull.label',
    { integration: accountingSoftwareName },
  );
  const pullHelpText = [
    t(
      'bookkeep.integrations.settings.analyticalFieldMapping.syncSettings.pull.descriptions.line1',
      { integration: accountingSoftwareName },
    ),
    t(
      'bookkeep.integrations.settings.analyticalFieldMapping.syncSettings.pull.descriptions.line2',
      { integration: accountingSoftwareName },
    ),
    t(
      'bookkeep.integrations.settings.analyticalFieldMapping.syncSettings.pull.descriptions.line3',
      { integration: accountingSoftwareName },
    ),
  ];

  const mapLabel = t(
    'bookkeep.integrations.settings.analyticalFieldMapping.syncSettings.map.label',
    { integration: accountingSoftwareName },
  );

  const mapHelpText = [
    t(
      'bookkeep.integrations.settings.analyticalFieldMapping.syncSettings.map.descriptions.line1',
      { integration: accountingSoftwareName },
    ),
    t(
      'bookkeep.integrations.settings.analyticalFieldMapping.syncSettings.map.descriptions.line2',
      { integration: accountingSoftwareName },
    ),
    t(
      'bookkeep.integrations.settings.analyticalFieldMapping.syncSettings.map.descriptions.line3',
      { integration: accountingSoftwareName },
    ),
  ];

  const noSyncLabel = t(
    'bookkeep.integrations.settings.analyticalFieldMapping.syncSettings.noSync.label',
  );
  const noSyncHelpText = [
    t(
      'bookkeep.integrations.settings.analyticalFieldMapping.syncSettings.noSync.descriptions.line1',
      { integration: accountingSoftwareName },
    ),
    t(
      'bookkeep.integrations.settings.analyticalFieldMapping.syncSettings.noSync.descriptions.line2',
      { integration: accountingSoftwareName },
    ),
    t(
      'bookkeep.integrations.settings.analyticalFieldMapping.syncSettings.noSync.descriptions.line3',
      { integration: accountingSoftwareName },
    ),
  ];

  const [selectedSyncSetting, setSelectedSyncSetting] = useState<SyncOption>();

  const isEnabled = (mode: SyncMode) => {
    const allowList = allowedSyncSettings ?? [];
    if (!allowList.length) {
      return true;
    }

    return allowList.includes(mode);
  };

  const buildSyncOption = (syncMode: SyncMode): SyncOption | undefined => {
    if (!isEnabled(syncMode)) {
      return undefined;
    }

    switch (syncMode) {
      case 'pull':
        return {
          key: 'pull',
          label: pullLabel,
          notes: pullHelpText,
          iconName: 'arrow-uturn-left-down',
          iconVariant: 'blue',
        };
      case 'pullAndMap':
        return {
          key: 'pullAndMap',
          label: mapLabel,
          notes: mapHelpText,
          iconName: 'link',
          iconVariant: 'forest',
        };
      case 'none':
        return {
          key: 'none',
          label: noSyncLabel,
          notes: noSyncHelpText,
          iconName: 'cross',
          iconVariant: 'apricot',
        };
      default:
        return undefined;
    }
  };

  useEffect(() => {
    if (value) {
      setSelectedSyncSetting(buildSyncOption(value));
    }
  }, [value]);

  const options: SyncOption[] = [
    buildSyncOption('pull'),
    buildSyncOption('pullAndMap'),
    buildSyncOption('none'),
  ].filter(isNotNullable);

  const handleChange = (option: SyncOption) => {
    setSelectedSyncSetting(option);
    onChange(option.key);
  };

  return (
    <div className="w-full">
      <FormField
        label={t(
          'bookkeep.integrations.settings.analyticalFieldMapping.fullScreenModal.reviewSyncSettingsTitle',
          { fieldName, integration: accountingSoftwareName },
        )}
      >
        <Select<SyncOption>
          value={selectedSyncSetting}
          onSelect={handleChange}
          dropdownContentMaxHeight="432px"
          fit="parent"
          options={options}
          renderOption={({ key, label, notes, iconName, iconVariant }) => (
            <DropdownItem
              key={key}
              isSelected={selectedSyncSetting?.key === key}
              label={label}
              helpText={<SyncOptionHelpText notes={notes} />}
              prefix={
                <HighlightIcon
                  size={32}
                  name={iconName}
                  variant={iconVariant}
                />
              }
            />
          )}
        />
      </FormField>
    </div>
  );
};
